import type { QueryClient } from '@tanstack/react-query';

import { SCAN_HISTORY_STORAGE_KEY } from '@/hooks/usePersistedScanHistory';
import {
  DASHBOARD_VIEW_ADMIN_KEY,
  DASHBOARD_VIEW_KITCHEN_KEY,
} from '@/lib/context/DashboardViewContext';
import { authTokenCookieName, isSessionCookieName } from '@/lib/supabase/sessionCookies';
import { trackingManager } from '@/lib/tracking/tracking-manager-instance';

/**
 * localStorage keys belonging to the signed-in person rather than to the device.
 *
 * Imported from the modules that own them rather than retyped, so renaming a key at
 * its source is a compile error here instead of a sweep that silently stops covering it.
 */
const IDENTITY_SCOPED_KEYS = [
  SCAN_HISTORY_STORAGE_KEY,
  DASHBOARD_VIEW_ADMIN_KEY,
  DASHBOARD_VIEW_KITCHEN_KEY,
];

// Deliberately NOT cleared, because these describe the device and not the person:
// 'scanner-preference' and 'qr-scanner-camera-preference' (which physical scanner and
// camera this terminal uses), 'nfc-unavailable' (this browser's NFC capability),
// 'ios-install-prompt-dismissed' and 'push-notification-prompt-decision-v1' ("stop
// asking me on this device"), the next-themes and 'vite-ui-theme' appearance keys, and
// the table/display layout keys ('employee-table-column-visibility',
// 'meal-distribution-display-size', 'meal-distribution-show-all-dayparts').
//
// Also deliberately kept: 'meal-rating-reminder-dismissed:<orderSlotId>'. Those key
// names embed the previous user's order slots, but the slot IDs are opaque UUIDs that
// reveal nothing, and a different user's slots never collide with them. Clearing them
// buys no privacy and costs real usability: it re-opens the rating modal on every
// login, over the page, on terminals where staff sign in and out all shift. Their
// unbounded growth is a genuine bug, but it belongs to the dialog that writes them.
//
// Bite Club runs on shared kitchen terminals where staff sign in and out across shifts.
// Wiping hardware settings on every sign-out would re-prompt the next person for
// choices that have nothing to do with who they are, and it protects no one: none of
// these values identify a user or a tenant. Do not add them to the lists above.

function cookieNames(): string[] {
  return document.cookie.split(';').map((pair) => pair.trim().split('=')[0]);
}

/**
 * Whether this document can see this project's session cookie: the base cookie or its
 * chunk 0, which is what @supabase/ssr reads a session back from.
 */
export function hasAuthTokenCookie(): boolean {
  if (typeof document === 'undefined') return false;
  const base = authTokenCookieName();
  if (!base) return false;
  try {
    const names = new Set(cookieNames());
    return names.has(base) || names.has(`${base}.0`);
  } catch {
    return false;
  }
}

/**
 * Deletes this project's session cookie wherever it carries no Domain attribute.
 *
 * Before the cookie domain was unified, server actions wrote the session cookie
 * host-only while the browser client and the proxy wrote it domain-scoped. A browser
 * keeps both: they are distinct cookies that happen to share a name. Supabase's sign-out
 * clears only the domain-scoped one it knows about, so a session that predates that fix
 * leaves the host-only copy behind. It cannot re-authenticate — the session is revoked
 * server-side — but a stale credential on disk is not something to rely on being inert.
 *
 * Done in the browser on purpose, and only once `logout()` has succeeded. A Server
 * Action's cookie store keeps one pending Set-Cookie per name, so a host-only delete
 * issued from the action would REPLACE the Domain-scoped delete Supabase queued rather
 * than join it, and the live cookie would survive the response. `document.cookie` has
 * no such limit, and the auth cookies are not httpOnly. A delete without a Domain only
 * ever targets the host-only variant, so the domain-scoped cookie is left to Supabase.
 */
export function removeHostOnlyAuthCookies(): void {
  if (typeof document === 'undefined') return;
  const base = authTokenCookieName();
  if (!base) return;

  try {
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    for (const name of new Set(cookieNames())) {
      if (!isSessionCookieName(base, name)) continue;
      document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=Lax${secure}`;
    }
  } catch {
    // Cookie access blocked by policy. The session is already revoked server-side.
  }
}

function removeIdentityScopedStorage(): void {
  if (typeof window === 'undefined') return;

  for (const key of IDENTITY_SCOPED_KEYS) {
    try {
      localStorage.removeItem(key);
    } catch {
      // One unwritable key must not strand the rest on disk, so swallow per key
      // rather than around the loop.
    }
  }
}

/**
 * Drops every piece of client state belonging to the user who is signing out.
 *
 * Query keys in the registry are flat literals (`['employees']`, `['dashboard-metrics']`,
 * `['user-tenant']`) with no tenant segment, and the QueryClient is a module-scope
 * singleton that survives client-side navigation. Without this, the next person to sign
 * in through the same document reads the previous tenant's rows.
 *
 * `queryClient.clear()` empties the cache but does NOT blank what mounted observers are
 * already rendering: they keep their last snapshot, and provoke no refetch. Verified
 * against React Query v5. The hard navigation in `useSignOut` is what removes the pixels.
 *
 * @param resetTracking pass false when a new user is signing in, so this does not wipe
 * the analytics identity that the login page is establishing for them.
 */
export function clearClientSessionState(
  queryClient: QueryClient,
  { resetTracking = true }: { resetTracking?: boolean } = {},
): void {
  queryClient.clear();
  removeIdentityScopedStorage();

  if (!resetTracking) return;

  try {
    // reset(), not disableTracking(): the latter empties the tracker array, which would
    // silently drop the events the login page fires next.
    trackingManager.reset();
  } catch (error) {
    console.error('Failed to reset tracking identity on sign-out:', error);
  }
}
